import type { VoidFunctionComponent } from 'react';

import { useParams } from '../../../contexts/RouterContext';
import { useReportType } from '../hooks';
import { isReleaseGroupScope, type ReportsV3Params, type ReportType } from '../types';

import styles from './Heading.module.less';

/**
 * Helper so that we don't need to do the same tedious string interpolation for the scope label.
 * @param {boolean} isReleaseGroup - Whether the scope is a release group.
 * @returns {'project' | 'release group'} The label for the scope.
 */
const labelForScope = (isReleaseGroup: boolean): 'project' | 'release group' =>
  isReleaseGroup ? 'release group' : 'project';

type HeadingConfig = {
  heading: string;
  getDescription: (scopeLabel: 'project' | 'release group') => string;
};

const headingsMap: Readonly<Record<ReportType, HeadingConfig>> = {
  licensing: {
    heading: 'Licensing',
    getDescription: scopeLabel =>
      `Summarizes the licenses used across your ${scopeLabel}'s dependencies to help you stay compliant and audit-ready.`,
  },
  sbom: {
    heading: 'SBOM',
    getDescription: scopeLabel =>
      `A detailed inventory of all open-source and third-party components used in your ${scopeLabel}, including their versions, licenses, and relationships.`,
  },
  vulnerability: {
    heading: 'Vulnerabilities',
    getDescription: scopeLabel =>
      `Generate a vulnerability report for your ${scopeLabel}.`,
  },
  'remediation-guidance': {
    heading: 'Remediation Guidance',
    getDescription: scopeLabel =>
      `Generate a remediation guidance plan for your ${scopeLabel}.`,
  },
};

/**
 * A component that displays the heading and description for the report.
 */
export const Heading: VoidFunctionComponent = () => {
  const params = useParams<ReportsV3Params>();
  const reportType = useReportType();
  const isReleaseGroup = isReleaseGroupScope(params);
  const scopeLabel = labelForScope(isReleaseGroup);

  return (
    <header className={styles.header}>
      <h1 className={styles.heading}>
        {headingsMap[reportType]?.heading ?? 'Report'}
      </h1>
      <p className={styles.description}>
        {headingsMap[reportType]?.getDescription(scopeLabel) ?? `Generate a report for your ${scopeLabel}.`}
      </p>
    </header>
  );
};
